import { useCallback, useContext, useState } from 'react';

import { apiClient } from 'Helpers/network';

import ToastContext from 'Context/ToastContext';

import { useToast } from 'Hooks/useToast';

/**
 * Custom hook to make API requests using axios.
 *
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.url - The URL for the API request.
 * @param {Object} [params.options={}] - Additional axios options for the API request.
 * @param {Object} [params.options.data] - Request payload for POST, PUT, PATCH requests.
 * @param {Object} [params.options.params] - URL parameters for GET requests.
 * @param {Object} [params.options.headers] - URL headers for requests.
 * @param {boolean} [params.displayErrorToast=true] - Whether to display an error toast on request failure.
 * @param {string} [params.errorMessage] - Custom error message to display in the toast.
 * @param {string} [params.successMessage] - Custom success message to display in the toast.
 * @returns {Object} - The state and functions for the API request.
 * @returns {any} data - The data returned from the API request.
 * @returns {boolean} loading - The loading state of the API request.
 * @returns {Error|null} error - The error state of the API request.
 * @returns {Function} request - Function to make the API request with optional payload.
 * @returns {Function} get - Convenience method for GET requests.
 * @returns {Function} post - Convenience method for POST requests.
 * @returns {Function} put - Convenience method for PUT requests.
 * @returns {Function} patch - Convenience method for PATCH requests.
 * @returns {Function} delete - Convenience method for DELETE requests.
 */
const useApiRequest = ({ url, options = {}, displayErrorToast = true, errorMessage, successMessage } = {}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const toast = useContext(ToastContext) ? useToast() : null;

    /**
     * Makes an API request with the given options
     */
    const request = useCallback(
        async (requestOptions = {}) => {
            setLoading(true);
            setError(null);

            try {
                if (requestOptions.replace && Object.keys(requestOptions.replace).length > 0) {
                    Object.entries(requestOptions.replace).forEach(([key, value]) => {
                        url = url.replace(key, value);
                    });
                }

                const response = await apiClient({
                    url,
                    ...options,
                    ...requestOptions,
                });

                setData(response.data);

                if (toast && successMessage) {
                    toast.success(successMessage);
                }

                return Promise.resolve(response);
            } catch (err) {
                setError(err);

                if (toast && displayErrorToast && err.response?.data?.metadata?.show_toast !== false) {
                    toast.error(errorMessage || err.response?.data?.message || err.response?.data?.error || $t('An error occurred, please try again'));
                }

                return Promise.reject(err);
            } finally {
                setLoading(false);
            }
        },
        [url, options, toast, displayErrorToast, errorMessage, successMessage],
    );

    const reset = useCallback(() => {
        setData(null);
        setLoading(false);
        setError(null);
    }, []);

    // Convenience methods for different HTTP methods
    const get = useCallback((params, replace) => request({ method: 'get', params, replace }), [request]);
    const post = useCallback((data, replace) => request({ method: 'post', data, replace }), [request]);
    const put = useCallback((data, replace) => request({ method: 'put', data, replace }), [request]);
    const patch = useCallback((data, replace) => request({ method: 'patch', data, replace }), [request]);
    const del = useCallback((data, replace) => request({ method: 'delete', data, replace }), [request]);

    return {
        data,
        loading,
        error,
        request,
        reset,
        get,
        post,
        put,
        patch,
        delete: del, // 'delete' is a reserved word in JavaScript
    };
};

export default useApiRequest;
